import { useState, useMemo } from 'react';
import {
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCurrentCalendarColor } from '../contexts/CurrentCalendarContext';
import { useCalendarPage } from '../contexts/CalendarPageContext';
import { EventPanelProvider } from '../contexts/EventPanelContext';
import SideDrawer, { SideDrawerHeader } from './SideDrawer';
import { useCalendars } from '../hooks/useCalendars';
import {
  useAddEvent,
  useUpdateEvent,
  useDeleteEvent,
  useCreateExceptionEvent,
  useMirrorEvent,
  useUnmirrorEvent,
} from '../hooks/useEvents';
import { api } from '../lib/api';
import { buildRRule, parseRRule } from '../lib/rrule';
import EventForm from './EventForm';
import EventView from './EventView';
import DeleteRecurringEventDialog, {
  type DeleteScope,
} from './DeleteRecurringEventDialog';
import type { ConflictWarning } from 'commoncal-shared';
import type { EventFormValues } from '../schemas/eventSchema';
import {
  getDefaultFormValues,
  isoToLocalDatetime,
  localDatetimeToIso,
} from '../schemas/eventSchema';

interface Props {
  mode: 'create' | 'edit';
  calendarId: string;
  defaultDate?: Date;
  eventId?: string;
  occurrenceDate?: string;
  onClose: () => void;
  /** When true, renders without SideDrawer wrapper (e.g. inside MobileDayView) */
  embedded?: boolean;
}

export default function EventPanel({
  mode,
  calendarId,
  defaultDate,
  eventId,
  occurrenceDate,
  onClose,
  embedded = false,
}: Props) {
  const accentColor = useCurrentCalendarColor();
  const { data: calendars = [] } = useCalendars();
  const { currentCalendar: calendarPageCalendar } =
    useCalendarPage();
  const queryClient = useQueryClient();

  // Get current calendar info for kocal handling
  // Prefer calendar from CalendarPageContext (includes join-token access)
  // Fall back to calendars list for regular member access
  const currentCalendar =
    calendarPageCalendar ||
    calendars.find(c => c.id === calendarId);
  const isKocal = currentCalendar?.is_kocal ?? false;
  const userRole = currentCalendar?.role;
  const calendarName = currentCalendar?.name;

  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me.get(),
  });
  const myUserId = meData?.user?.id;

  const { data: existingEvent, isLoading: isLoadingEvent } =
    useQuery({
      queryKey: ['event', eventId],
      queryFn: () => api.events.get(eventId!),
      select: d => d.event,
      enabled: mode === 'edit' && Boolean(eventId),
    });

  // Fetch current mirrors for this event
  const { data: mirrorsData } = useQuery({
    queryKey: ['event-mirrors', eventId],
    queryFn: () => api.events.listMirrors(eventId!),
    select: d => d.mirrors,
    enabled: mode === 'edit' && Boolean(eventId),
  });

  // Build a set of calendar IDs this event is mirrored to
  const mirroredCalendarIds = useMemo(() => {
    return new Set(
      mirrorsData?.map(m => m.target_calendar_id) ?? []
    );
  }, [mirrorsData]);

  const addEvent = useAddEvent(calendarId);
  const updateEvent = useUpdateEvent(calendarId);
  const deleteEvent = useDeleteEvent(calendarId);
  const createException =
    useCreateExceptionEvent(calendarId);
  const mirrorEvent = useMirrorEvent(calendarId);
  const unmirrorEvent = useUnmirrorEvent(calendarId);

  const [conflicts, setConflicts] =
    useState<ConflictWarning>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Owners/managers can edit any event; members can only edit events they created
  const canEdit =
    mode === 'create' ||
    userRole === 'owner' ||
    userRole === 'manager' ||
    (existingEvent?.created_by != null &&
      existingEvent.created_by === myUserId);
  const [saveScope, setSaveScope] = useState<
    'template' | 'instance'
  >('template');
  // Existing events open in view mode; only users with write permission can switch to edit.
  const [isEditing, setIsEditing] = useState(
    mode === 'create'
  );
  // Pre-selected mirror targets for create mode (local state only; applied on submit)
  const [pendingMirrorIds, setPendingMirrorIds] = useState<
    Set<string>
  >(new Set());
  // Whether the "add to shared calendar" dropdown is open
  const [showMirrorDropdown, setShowMirrorDropdown] =
    useState(false);

  const isPending =
    addEvent.isPending ||
    updateEvent.isPending ||
    deleteEvent.isPending ||
    createException.isPending;

  // Build default values from existing event or date
  const defaultValues = useMemo<EventFormValues>(() => {
    if (mode === 'edit' && existingEvent) {
      let startAtLocal: string;
      let endAtLocal: string;
      if (
        existingEvent.all_day &&
        /^\d{4}-\d{2}-\d{2}$/.test(
          existingEvent.start_date ?? ''
        ) &&
        /^\d{4}-\d{2}-\d{2}$/.test(
          existingEvent.end_date ?? ''
        )
      ) {
        startAtLocal = existingEvent.start_date + 'T00:00';
        endAtLocal = existingEvent.end_date + 'T00:00';
      } else {
        startAtLocal = isoToLocalDatetime(
          existingEvent.start_at,
          existingEvent.all_day
        );
        endAtLocal = isoToLocalDatetime(
          existingEvent.end_at,
          existingEvent.all_day
        );
      }
      return {
        calendarId: existingEvent.calendar_id,
        title: existingEvent.title,
        description: existingEvent.description ?? '',
        address: existingEvent.address ?? '',
        phone: existingEvent.phone ?? '',
        startAt: startAtLocal,
        endAt: endAtLocal,
        allDay: existingEvent.all_day,
        tags: existingEvent.tags,
        recurrence: parseRRule(
          existingEvent.rrule ?? ''
        ) ?? { type: 'none' },
        asKocal: false,
      };
    }
    const defaults = getDefaultFormValues(
      defaultDate ?? new Date()
    );
    return {
      ...defaults,
      calendarId,
      // Owner default: ko-cal behavior supersedes
      asKocal:
        mode === 'create' && isKocal && userRole === 'owner'
          ? true
          : defaults.asKocal,
    };
  }, [mode, existingEvent, defaultDate, calendarId]);

  async function handleSubmit(values: EventFormValues) {
    setError(null);
    setConflicts(null);

    const rrule = buildRRule(values.recurrence);

    try {
      if (mode === 'create') {
        // When a member is on a kocal, or when "as myself" is selected on a
        // kocal, use the contribute endpoint which creates the event on the
        // personal calendar and mirrors it to the kocal.
        if (
          isKocal &&
          (userRole === 'member' || !values.asKocal)
        ) {
          const result = await api.calendars.contribute(
            calendarId,
            {
              title: values.title.trim(),
              description:
                values.description.trim() || undefined,
              address: values.address?.trim() || undefined,
              phone: values.phone?.trim() || undefined,
              start_at: localDatetimeToIso(
                values.startAt,
                values.allDay
              ),
              end_at: localDatetimeToIso(
                values.endAt,
                values.allDay
              ),
              all_day: values.allDay,
              tags: values.tags,
              rrule: rrule ?? undefined,
            }
          );
          // Invalidate both calendars to show the new event
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: ['events', calendarId],
            }),
            queryClient.invalidateQueries({
              queryKey: [
                'events',
                result.event.calendar_id,
              ],
            }),
          ]);
          if (result.conflicts?.warned) {
            setConflicts(result.conflicts);
          } else {
            onClose();
          }
        } else {
          const payload = {
            calendar_id: values.calendarId,
            title: values.title.trim(),
            description:
              values.description.trim() || undefined,
            address: values.address?.trim() || undefined,
            phone: values.phone?.trim() || undefined,
            start_at: localDatetimeToIso(
              values.startAt,
              values.allDay
            ),
            end_at: localDatetimeToIso(
              values.endAt,
              values.allDay
            ),
            start_date: values.allDay
              ? values.startAt.slice(0, 10)
              : undefined,
            end_date: values.allDay
              ? values.endAt.slice(0, 10)
              : undefined,
            all_day: values.allDay,
            tags: values.tags,
            rrule: rrule ?? undefined,
            as_kocal: values.asKocal,
          };
          const result = await addEvent.mutateAsync({
            input: payload,
          });
          const { conflicts: warned } = result;
          // Split pending targets by behavior:
          // - Ko-cal targets: mirror from the created event
          // - Shared calendar targets: create a separate event directly on that
          //   calendar (no mirror relationship)
          const targetCalendars = calendars.filter(c =>
            pendingMirrorIds.has(c.id)
          );
          const mirrorTargetIds = targetCalendars
            .filter(c => c.is_kocal)
            .map(c => c.id);
          const sharedTargetIds = targetCalendars
            .filter(c => !c.is_kocal)
            .map(c => c.id);
          await Promise.all([
            ...mirrorTargetIds.map(targetCalendarId =>
              api.events.mirror(
                result.event.id,
                targetCalendarId
              )
            ),
            ...sharedTargetIds.map(targetCalendarId =>
              addEvent.mutateAsync({
                input: {
                  ...payload,
                  calendar_id: targetCalendarId,
                  as_kocal: false,
                },
              })
            ),
          ]);
          if (warned?.warned) {
            setConflicts(warned);
          } else {
            onClose();
          }
        }
      } else if (mode === 'edit' && eventId) {
        // Handle recurring event scope
        if (
          existingEvent?.rrule &&
          saveScope === 'instance'
        ) {
          if (!occurrenceDate) {
            setError(
              'Cannot create exception: missing occurrence date'
            );
            return;
          }
          const { conflicts: warned } =
            await createException.mutateAsync({
              calendar_id: values.calendarId,
              title: values.title.trim(),
              description: values.description.trim(),
              start_at: localDatetimeToIso(
                values.startAt,
                values.allDay
              ),
              end_at: localDatetimeToIso(
                values.endAt,
                values.allDay
              ),
              start_date: values.allDay
                ? values.startAt.slice(0, 10)
                : undefined,
              end_date: values.allDay
                ? values.endAt.slice(0, 10)
                : undefined,
              all_day: values.allDay,
              recurrence_id: eventId,
              original_occurrence_date: occurrenceDate,
            });
          if (warned?.warned) {
            setConflicts(warned);
          } else {
            onClose();
          }
        } else {
          const startDate = values.allDay
            ? values.startAt.slice(0, 10)
            : undefined;
          const endDate = values.allDay
            ? values.endAt.slice(0, 10)
            : undefined;
          const { conflicts: warned } =
            await updateEvent.mutateAsync({
              id: eventId,
              title: values.title.trim(),
              description:
                values.description.trim() || null,
              address: values.address?.trim() || null,
              phone: values.phone?.trim() || null,
              start_at: localDatetimeToIso(
                values.startAt,
                values.allDay
              ),
              end_at: localDatetimeToIso(
                values.endAt,
                values.allDay
              ),
              start_date: startDate,
              end_date: endDate,
              all_day: values.allDay,
              tags: values.tags,
              rrule: rrule ?? null,
            });
          if (warned?.warned) {
            setConflicts(warned);
          } else {
            onClose();
          }
        }
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong'
      );
    }
  }

  async function handleDelete(scope: DeleteScope) {
    if (!eventId) return;
    try {
      await deleteEvent.mutateAsync({
        id: eventId,
        scope,
        occurrenceDate,
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Delete failed'
      );
      setConfirmDelete(false);
    }
  }

  async function handleSaveAnyway() {
    setConflicts(null);
    onClose();
  }

  const recurrenceDisabled =
    mode === 'edit' &&
    existingEvent?.rrule !== null &&
    existingEvent?.rrule !== undefined &&
    saveScope === 'instance';

  const extraContent = (() => {
    const sourceCalendarId =
      mode === 'edit'
        ? existingEvent?.calendar_id
        : calendarId;
    const allSharedCals = calendars.filter(cal => {
      if (cal.is_personal || cal.id === sourceCalendarId)
        return false;
      // Ko-cals can be mirrored by anyone subscribed or joined.
      // Shared calendars require direct write access (membership).
      if (cal.is_kocal) {
        return cal.is_member || cal.is_subscribed;
      }
      return cal.is_member;
    });
    // IDs currently selected (create = pending set, edit = live mirrors)
    const selectedIds =
      mode === 'create'
        ? pendingMirrorIds
        : mirroredCalendarIds;
    // Calendars not yet selected — available in the dropdown
    const availableCals = allSharedCals.filter(
      cal => !selectedIds.has(cal.id)
    );

    function removeSelection(calId: string) {
      if (mode === 'create') {
        setPendingMirrorIds(prev => {
          const next = new Set(prev);
          next.delete(calId);
          return next;
        });
      } else if (eventId) {
        unmirrorEvent.mutate({
          eventId,
          targetCalendarId: calId,
        });
      }
    }

    function addSelection(calId: string) {
      if (!calId) return;
      if (mode === 'create') {
        setPendingMirrorIds(prev => {
          const next = new Set(prev);
          next.add(calId);
          return next;
        });
      } else if (eventId) {
        mirrorEvent.mutate({
          eventId,
          targetCalendarId: calId,
        });
      }
      setShowMirrorDropdown(false);
    }

    const showSection =
      (mode === 'create' ||
        (mode === 'edit' && Boolean(existingEvent))) &&
      canEdit;

    return (
      <>
        {/* Recurring event save scope selector (edit mode only) */}
        {mode === 'edit' &&
          canEdit &&
          existingEvent?.rrule && (
            <div className="px-3 py-2 bg-paper-100 rounded-sm">
              <label className="block font-mono text-xs text-ink-400 mb-1.5">
                Apply changes to:
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSaveScope('template')}
                  className={[
                    'flex-1 px-2 py-1.5 rounded-sm text-xs font-mono transition-colors border',
                    saveScope === 'template'
                      ? 'text-white shadow-sm'
                      : 'bg-paper-200 text-ink-600 border-paper-300 hover:bg-paper-300',
                  ].join(' ')}
                  style={
                    saveScope === 'template'
                      ? {
                          backgroundColor: accentColor,
                          borderColor: accentColor,
                        }
                      : undefined
                  }
                >
                  All events
                </button>
                <button
                  type="button"
                  onClick={() => setSaveScope('instance')}
                  className={[
                    'flex-1 px-2 py-1.5 rounded-sm text-xs font-mono transition-colors border',
                    saveScope === 'instance'
                      ? 'text-white shadow-sm'
                      : 'bg-paper-200 text-ink-600 border-paper-300 hover:bg-paper-300',
                  ].join(' ')}
                  style={
                    saveScope === 'instance'
                      ? {
                          backgroundColor: accentColor,
                          borderColor: accentColor,
                        }
                      : undefined
                  }
                >
                  This event only
                </button>
              </div>
            </div>
          )}

        {/* Conflict notice */}
        {conflicts?.warned && (
          <div className="flex items-center justify-between gap-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-sm">
            <p className="font-mono text-xs text-amber-800">
              conflicts with {conflicts.count} existing
              event
              {conflicts.count !== 1 ? 's' : ''}
            </p>
            <button
              type="button"
              onClick={handleSaveAnyway}
              className="font-mono text-xs text-amber-700 underline shrink-0"
            >
              OK, dismiss
            </button>
          </div>
        )}

        {error && (
          <p className="font-mono text-xs text-red-600">
            {error}
          </p>
        )}

        {/* Mirror to shared calendar — add-button + rows */}
        {showSection && (
          <div className="space-y-1.5">
            {/* Selected calendar rows */}
            {allSharedCals
              .filter(cal => selectedIds.has(cal.id))
              .map(cal => (
                <div
                  key={cal.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 bg-paper-100 rounded-sm"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        backgroundColor: cal.color,
                      }}
                    />
                    <span className="font-mono text-xs text-ink-700 truncate">
                      {cal.name}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSelection(cal.id)}
                    disabled={
                      mode === 'edit' &&
                      (mirrorEvent.isPending ||
                        unmirrorEvent.isPending)
                    }
                    aria-label={`Remove ${cal.name}`}
                    className="font-mono text-xs text-ink-300 hover:text-red-500 transition-colors shrink-0 px-1 disabled:opacity-40"
                  >
                    ✕
                  </button>
                </div>
              ))}

            {/* Dropdown when open */}
            {showMirrorDropdown &&
              availableCals.length > 0 && (
                <select
                  className="w-full input-field font-mono text-xs"
                  defaultValue=""
                  autoFocus
                  onChange={e => {
                    addSelection(e.target.value);
                  }}
                >
                  <option value="" disabled>
                    Select a shared calendar…
                  </option>
                  {availableCals.map(cal => (
                    <option key={cal.id} value={cal.id}>
                      {cal.name}
                    </option>
                  ))}
                </select>
              )}

            {/* Add button — hidden when all eligible are already selected */}
            {availableCals.length > 0 &&
              !showMirrorDropdown && (
                <button
                  type="button"
                  onClick={() =>
                    setShowMirrorDropdown(true)
                  }
                  className="font-mono text-xs text-ink-400 hover:text-ink-700 transition-colors py-1"
                >
                  + Add to shared calendar
                </button>
              )}
          </div>
        )}

        {/* Delete button (edit mode only, only if user can edit) */}
        {mode === 'edit' && canEdit && (
          <>
            {confirmDelete && existingEvent?.rrule ? (
              <DeleteRecurringEventDialog
                eventTitle={existingEvent.title}
                occurrenceDate={occurrenceDate}
                isOpen={confirmDelete}
                onCancel={() => setConfirmDelete(false)}
                onConfirm={handleDelete}
                isDeleting={deleteEvent.isPending}
              />
            ) : confirmDelete ? (
              <div className="space-y-2">
                <p className="font-mono text-xs text-red-600 text-center">
                  Delete this event? This cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="btn-ghost flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete('all')}
                    disabled={deleteEvent.isPending}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-sm text-sm font-mono hover:bg-red-700 transition-colors disabled:opacity-40"
                  >
                    {deleteEvent.isPending
                      ? 'Deleting…'
                      : 'Delete'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="w-full font-mono text-xs text-red-500 hover:text-red-700 py-1.5 transition-colors"
              >
                Delete event
              </button>
            )}
          </>
        )}
      </>
    );
  })();

  const contextValue = {
    mode,
    event: existingEvent ?? null,
    defaultValues,
    canEdit,
    isEditing,
    accentColor,
    calendarName,
    isKocal,
    userRole,
    isPending,
    recurrenceDisabled,
    enterEditMode: () => setIsEditing(true),
    exitEditMode: () => setIsEditing(false),
    onClose,
    onSubmit: handleSubmit,
    extraContent,
  };

  const inner = (
    <div className="flex flex-col h-full">
      {!embedded && (
        <SideDrawerHeader
          title={
            mode === 'create'
              ? 'New Event'
              : isEditing
                ? 'Edit Event'
                : 'View Event'
          }
          accentColor={accentColor}
          onClose={onClose}
        />
      )}

      {mode === 'edit' &&
      (isLoadingEvent || !existingEvent) ? (
        <div className="flex-1 flex items-center justify-center">
          <span className="font-mono text-sm text-ink-400">
            Loading…
          </span>
        </div>
      ) : (
        <>
          {mode === 'create' || isEditing ? (
            <EventForm />
          ) : (
            <EventView />
          )}
        </>
      )}
    </div>
  );

  return (
    <EventPanelProvider value={contextValue}>
      {embedded ? (
        inner
      ) : (
        <SideDrawer onClose={onClose}>{inner}</SideDrawer>
      )}
    </EventPanelProvider>
  );
}
